import React, { useState } from 'react';
import { Modal } from '../../common/Modal';
import Button from '../../common/Button';
import FormField, { Select, Input, Textarea } from '../../common/FormField';

// Extracted verbatim from JobManagement's JSX (Task 5), converted to the
// shared Modal primitive and B2 tokens (Task 8). This was a rewrite, not a
// swap: the original carried 32 inline style objects and one className.
//
// isOpen is hardcoded true: the orchestrator's `showStatusModal &&` guard
// at the call site is what controls mounting.
//
// Defect A (dead date-picker guard): `isDatePickerOpen` is deleted rather
// than wired up. The "date picker" here is a synchronous window.prompt()
// call — while it is open, the browser blocks all other event dispatch, so
// no backdrop click can reach the page regardless of any guard. The state
// was inert from day one and protects against a scenario that cannot
// structurally occur, so it is removed from this component, from
// useJobStatus.js, and from JobManagement.js rather than carried forward.
// Shown instead of the form once the save came back with a pay link or a
// warning. Clipboard handling follows ShareLinkPanel's copy().
const PayResult = ({ payResult, closeStatusModal }) => {
  const [copyStatus, setCopyStatus] = useState('');
  const copy = async () => {
    setCopyStatus('');
    try {
      await navigator.clipboard.writeText(payResult.url);
      setCopyStatus('Copied');
    } catch {
      // Clipboard unavailable or denied: the read-only input stays as the fallback.
      setCopyStatus('Select the link and copy it manually');
    }
  };
  const balance = `$${Number(payResult.balance || 0).toFixed(2)}`;

  return (
    <>
      <div className="flex flex-col gap-3">
        {payResult.url && (
          <>
            <p className="text-sm font-medium text-ink">
              Customer emailed a link to pay {balance}
            </p>
            <div className="flex gap-2 items-center">
              <input
                readOnly
                value={payResult.url}
                aria-label="Payment link"
                onFocus={(e) => e.target.select()}
                className="flex-1 min-h-touch border border-border-strong rounded-md px-3 py-2 text-sm"
              />
              <Button type="button" variant="secondary" onClick={copy}>Copy link</Button>
            </div>
            <span role="status" aria-live="polite" className="block text-small text-ink-muted">
              {copyStatus}
            </span>
          </>
        )}
        {payResult.warning && (
          <div role="alert" className="rounded-card border border-warning-border bg-warning-tint p-3 text-sm text-ink">
            {payResult.warning}
          </div>
        )}
      </div>
      <div className="-mx-6 -mb-4 mt-4 px-6 py-4 border-t border-border-default flex justify-end">
        <Button type="button" onClick={closeStatusModal}>Done</Button>
      </div>
    </>
  );
};

const StatusUpdateModal = ({
  statusUpdateForm,
  setStatusUpdateForm,
  updateJobStatus,
  closeStatusModal,
  selectedJobForActions,
  payResult
}) => payResult ? (
  <Modal isOpen onClose={closeStatusModal} title="Job status updated" size="sm">
    <PayResult payResult={payResult} closeStatusModal={closeStatusModal} />
  </Modal>
) : (
  <Modal
    isOpen
    onClose={closeStatusModal}
    title="Update Job Status"
    size="sm"
  >
    <div className="mb-4">
      <p className="text-sm text-ink-soft">Job: {selectedJobForActions.job_number}</p>
    </div>

    <div className="flex flex-col gap-4">
      <FormField label="New Status">
        <Select
          value={statusUpdateForm.status}
          onChange={(e) => {
            console.log('Status changed to:', e.target.value);
            setStatusUpdateForm(prev => ({ ...prev, status: e.target.value }));
          }}
        >
          <option value="created">Created</option>
          <option value="diagnosing">Diagnosing</option>
          <option value="diagnosed">Diagnosed</option>
          <option value="estimating">Estimating</option>
          <option value="quoted">Quoted</option>
          <option value="approved">Approved</option>
          <option value="in_progress">In Progress</option>
          <option value="completed">Completed</option>
          <option value="cancelled">Cancelled</option>
          <option value="on_hold">On Hold</option>
        </Select>
      </FormField>

      <FormField label="Completion Date (Optional)">
        <div className="flex gap-2">
          <Input
            type="text"
            value={statusUpdateForm.completion_date ? new Date(statusUpdateForm.completion_date).toLocaleString() : ''}
            placeholder="Click to select date and time"
            readOnly
            onMouseDown={(e) => {
              e.preventDefault();
              // Set current date/time as default
              const now = new Date();
              const year = now.getFullYear();
              const month = String(now.getMonth() + 1).padStart(2, '0');
              const day = String(now.getDate()).padStart(2, '0');
              const hours = String(now.getHours()).padStart(2, '0');
              const minutes = String(now.getMinutes()).padStart(2, '0');
              const defaultDateTime = `${year}-${month}-${day}T${hours}:${minutes}`;
              setStatusUpdateForm(prev => ({ ...prev, completion_date: defaultDateTime }));
            }}
            className="flex-1 bg-surface-sunken cursor-pointer"
          />
          <button
            type="button"
            onMouseDown={(e) => {
              e.preventDefault();
              // Open a simple date picker
              const date = prompt('Enter completion date (YYYY-MM-DD HH:MM):',
                statusUpdateForm.completion_date ?
                new Date(statusUpdateForm.completion_date).toISOString().slice(0, 16).replace('T', ' ') :
                new Date().toISOString().slice(0, 16).replace('T', ' ')
              );
              if (date) {
                try {
                  const [datePart, timePart] = date.split(' ');
                  const [year, month, day] = datePart.split('-');
                  const [hours, minutes] = timePart.split(':');
                  const dateTimeString = `${year}-${month}-${day}T${hours}:${minutes}`;
                  setStatusUpdateForm(prev => ({ ...prev, completion_date: dateTimeString }));
                } catch (error) {
                  alert('Invalid date format. Please use YYYY-MM-DD HH:MM');
                }
              }
            }}
            className="px-3 py-2 bg-accent text-white rounded-control text-xs whitespace-nowrap hover:bg-accent-hover transition-colors"
            title="Select date and time"
          >
            Select Date
          </button>
          {statusUpdateForm.completion_date && (
            <button
              type="button"
              onMouseDown={(e) => {
                e.preventDefault();
                setStatusUpdateForm(prev => ({ ...prev, completion_date: '' }));
              }}
              className="px-3 py-2 bg-danger text-white rounded-control text-xs whitespace-nowrap hover:opacity-90 transition-colors"
              title="Clear date"
            >
              Clear
            </button>
          )}
        </div>
        {statusUpdateForm.completion_date && (
          <p className="text-xs text-ink-soft mt-1">
            Selected: {new Date(statusUpdateForm.completion_date).toLocaleString()}
          </p>
        )}
      </FormField>

      {/* Customer Notification Section */}
      <div className="mt-4 p-4 bg-info-tint rounded-card border border-border-default">
        <div className="flex items-center mb-3">
          <input
            type="checkbox"
            id="notify_customer"
            checked={statusUpdateForm.notify_customer}
            onChange={(e) => {
              setStatusUpdateForm(prev => ({ ...prev, notify_customer: e.target.checked }));
            }}
            disabled={!selectedJobForActions?.customer_email}
            className={`w-[18px] h-[18px] mr-2.5 accent-accent ${selectedJobForActions?.customer_email ? 'cursor-pointer' : 'cursor-not-allowed'}`}
          />
          <label
            htmlFor="notify_customer"
            className={`text-sm font-medium text-ink ${selectedJobForActions?.customer_email ? 'cursor-pointer' : 'cursor-not-allowed'}`}
          >
            📧 Notify Customer of Status Change
          </label>
        </div>

        {!selectedJobForActions?.customer_email && (
          <p className="text-xs text-ink-faint ml-7">
            No customer email on file
          </p>
        )}

        {selectedJobForActions?.customer_email && (
          <p className="text-xs text-ink-soft ml-7 mb-3">
            Email will be sent to: <strong>{selectedJobForActions.customer_email}</strong>
          </p>
        )}

        {statusUpdateForm.notify_customer && selectedJobForActions?.customer_email && (
          <div className="mt-3">
            <FormField label="Message to Customer (Optional)">
              <Textarea
                value={statusUpdateForm.customer_notes}
                onChange={(e) => {
                  setStatusUpdateForm(prev => ({ ...prev, customer_notes: e.target.value }));
                }}
                placeholder="Add a personal message for the customer... (e.g., 'Parts have arrived, we'll start work tomorrow morning!')"
              />
            </FormField>
          </div>
        )}
      </div>
    </div>

    <div className="-mx-6 -mb-4 mt-2 px-6 py-4 border-t border-border-default flex justify-end gap-3">
      <button
        onClick={closeStatusModal}
        className="px-4 py-2 border border-border-default rounded-card text-ink bg-surface-card hover:bg-surface-sunken transition-colors"
      >
        Cancel
      </button>
      <button
        onClick={updateJobStatus}
        className="px-4 py-2 bg-accent text-white rounded-card hover:bg-accent-hover transition-colors"
      >
        Update Status
      </button>
    </div>
  </Modal>
);

export default StatusUpdateModal;
